import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, MessageCircle, Languages } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/sptl1rmt_image%201.png";
const WHATSAPP_URL = "https://wa.me/919819933676?text=Namaste!%20Mujhe%20Divya%20Dhara%20ka%201%20din%20ka%20%E2%82%B984%20trial%20book%20karna%20hai.%20Please%20contact%20karein.%20%F0%9F%A5%9B";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [pendingBadge, setPendingBadge] = useState(0);
  const { user, logout } = useAuth();
  const { lang, toggle, t } = useI18n();
  const navigate = useNavigate();

  const navItems = [
    { to: "/", label: t.nav.home },
    { to: "/#founders", label: t.nav.founders },
    { to: "/gallery", label: t.nav.gallery },
    { to: "/#delivery", label: "Delivery" },
    { to: "/#contact", label: t.nav.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Admin: poll pending bookings for red dot badge
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const fetchPending = async () => {
      try {
        const r = await api.get("/admin/today-stats");
        setPendingBadge((r.data?.new_bookings_pending || 0) + (r.data?.open_tickets || 0));
      } catch (err) {
        console.warn("admin/today-stats poll failed:", err?.message);
      }
    };
    fetchPending();
    const id = setInterval(fetchPending, 60000);
    return () => clearInterval(id);
  }, [user]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all ${scrolled ? "bg-dd-green-deep/95 backdrop-blur-xl border-b border-dd-gold/25 shadow-[0_4px_30px_rgba(0,0,0,0.35)]" : "bg-dd-green-deep/70 backdrop-blur-md"}`}
    >
      <div className="dd-container flex items-center justify-between h-16 md:h-[88px] gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="Divya Dhara" className="w-11 h-11 md:w-14 md:h-14 object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif italic text-xl md:text-2xl text-dd-cream">Divya Dhara</div>
            <div className="dd-label text-[0.58rem] text-dd-gold tracking-[0.25em]">Har Boond Mein Amrit</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 mx-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-dd-gold" : "text-dd-cream hover:text-dd-gold"}`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={toggle}
            className="flex items-center gap-1.5 text-sm font-medium text-dd-cream hover:text-dd-gold border border-dd-gold/30 rounded-full px-3 py-2 transition-colors"
            data-testid="nav-lang-toggle"
            aria-label="Switch language"
          >
            <Languages className="w-4 h-4" strokeWidth={1.75} />
            <span className={lang === "en" ? "text-dd-gold" : "text-dd-cream-muted"}>EN</span>
            <span className="text-dd-cream-muted">|</span>
            <span className={lang === "hi" ? "text-dd-gold font-devanagari" : "text-dd-cream-muted font-devanagari"}>हिं</span>
          </button>

          {user && user !== false ? (
            <>
              <Link
                to={user.role === "admin" ? "/admin" : "/dashboard"}
                className="relative text-sm font-medium text-dd-cream hover:text-dd-gold flex items-center gap-1.5 px-3 py-2"
                data-testid="nav-dashboard"
              >
                <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> {user.role === "admin" ? "Admin" : "Dashboard"}
                {user.role === "admin" && pendingBadge > 0 && (
                  <span className="absolute -top-0.5 -right-1 bg-red-500 text-white text-[0.55rem] font-bold px-1.5 py-0.5 rounded-full min-w-[16px] inline-flex items-center justify-center animate-pulse" data-testid="admin-pending-badge">
                    {pendingBadge}
                  </span>
                )}
              </Link>
              <button onClick={handleLogout} className="text-sm font-medium text-dd-cream-muted hover:text-dd-gold flex items-center gap-1.5 px-3 py-2" data-testid="nav-logout">
                <LogOut className="w-4 h-4" strokeWidth={1.5} /> Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm font-medium text-dd-cream hover:text-dd-gold border border-dd-gold/40 hover:border-dd-gold rounded-full px-4 py-2 transition-colors"
                data-testid="nav-login"
              >
                Login
              </Link>
              <Link
                to="/login?admin=1"
                className="text-sm font-medium text-dd-gold hover:text-dd-gold-light flex items-center gap-1.5 px-2 py-2"
                data-testid="nav-admin"
                title="Admin Portal"
              >
                <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> Admin
              </Link>
            </>
          )}

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-b from-dd-gold to-[#8F6E28] hover:from-dd-gold-light hover:to-dd-gold text-dd-ink-dark px-5 py-2.5 rounded-full text-sm font-bold shadow-lg shadow-[rgba(201,162,76,0.25)] transition-all tracking-wide"
            data-testid="nav-book"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2.25} />
            Book Now
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-dd-cream"
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-dd-green-deep border-t border-dd-gold/20">
          <div className="dd-container py-5 flex flex-col gap-1">
            {navItems.map((item) => (
              <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="py-2.5 text-dd-cream hover:text-dd-gold font-medium" data-testid={`mobile-nav-${item.label}`}>
                {item.label}
              </Link>
            ))}
            <button onClick={() => { toggle(); }} className="py-2.5 text-left text-dd-cream font-medium flex items-center gap-2">
              <Languages className="w-4 h-4" /> {lang === "en" ? "हिंदी में देखें" : "View in English"}
            </button>
            <div className="h-px bg-dd-gold/20 my-2" />
            {user && user !== false ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="py-2 text-dd-gold font-medium">
                  {user.role === "admin" ? "Admin Dashboard" : "My Dashboard"}
                </Link>
                <button onClick={handleLogout} className="py-2 text-left text-dd-cream-muted font-medium">Logout</button>
              </>
            ) : (
              <div className="flex flex-col gap-2 mt-1">
                <Link to="/login" onClick={() => setOpen(false)} className="py-3 border border-dd-gold/40 text-dd-cream text-center rounded-full font-semibold" data-testid="mobile-nav-login">
                  Customer Login
                </Link>
                <Link to="/login?admin=1" onClick={() => setOpen(false)} className="py-3 text-dd-gold text-center text-sm font-medium" data-testid="mobile-nav-admin">
                  Admin Portal →
                </Link>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark py-3 rounded-full font-bold">
                  <MessageCircle className="w-4 h-4" /> Book Now on WhatsApp
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
